/**
 * Costs — the permanent home for every number the founder ever told us about
 * what their business spends (founder 2026-07-05: "when this onboarding is
 * done, it needs to live somewhere so they can update prices later — along
 * with all their other costs. Everything should be on the same page").
 *
 * Nothing here is a second editor: each section IS the original input surface,
 * reused — the onboarding costs chat (same saved conversation, same engine),
 * the Settings business-costs sections, and the Financials cash-timing card.
 * One page, zero drift.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CostsChat } from "./OnboardingChatPage.js";
import { BusinessCostsTab } from "./SettingsPage.js";
import { CashTimingCard } from "../components/CashTimingCard.js";
import { PayoutReservesCard } from "../components/PayoutReservesCard.js";
import { NeedsCostsStrip, ParkedCostsCard, InventoryFinancingCard, ProcessingFeesCard } from "../components/CostsPageCards.js";
import { useIsViewer } from "../lib/use-role.js";

export default function CostsPage(): React.ReactElement {
  const navigate = useNavigate();
  // The chat's endpoints are owner/admin-only — a viewer gets a plain
  // explanation instead of a dead chat throwing 403 toasts.
  const isViewer = useIsViewer();
  // "Cost this product" in the needs-attention strip drops a ready-made ask
  // into the chat's answer box and scrolls it into view — one click from
  // "this product has no costs" to the conversation that fixes it.
  const [chatPrefill, setChatPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const costProduct = (ask: string): void => {
    setChatPrefill((p) => ({ text: ask, nonce: (p?.nonce ?? 0) + 1 }));
    document.getElementById("costs-chat-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // Cross-page hand-off (the universal "we need something → open a chat"
  // pattern, founder 2026-07-11): a "Fix it →" click on any CM-gap warning
  // lands here with a ready-made ask in router state (CmGapNotice.tsx). Drop
  // it into the chat, then clear the state so back/refresh doesn't re-ask.
  const location = useLocation();
  useEffect(() => {
    const ask = (location.state as { costsAsk?: string } | null)?.costsAsk;
    if (!ask) return;
    costProduct(ask);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]);
  return (
    <div style={{ maxWidth: 1100 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 4px" }}>Costs</h1>
      <p style={{ fontSize: 13, color: "var(--body)", margin: "0 0 22px", lineHeight: 1.55 }}>
        Everything you've told us about what your business spends lives here. Change a number any
        time — your margins and profit update everywhere.
      </p>

      <NeedsCostsStrip onCostProduct={costProduct} canEdit={!isViewer} />

      {/* The "Check my numbers" run-button card was removed 2026-07-12: number
          verification is operator-side now (the agency Data-health page runs the
          Verify Device across the fleet nightly + on demand). What stays
          client-facing: the plain Needs-costs warnings + fix-via-chat above, and
          the subtle trust line on the home dashboard. */}

      <section id="costs-chat-section" style={{ marginBottom: 30 }}>
        <h2 style={sectionTitle}>Product costs &amp; prices</h2>
        <p style={sectionSub}>
          The same helper from setup. Tell it what changed — "the jars cost $8 now" — or upload a
          new cost sheet, and it fixes the numbers for you.
        </p>
        {isViewer ? (
          <div style={{ padding: "14px 16px", background: "var(--surface-2)", border: "1px dashed var(--border)", borderRadius: 10, fontSize: 12.5, color: "var(--body)" }}>
            Product costs are set by an owner or admin. You can see the results on the Products page.
          </div>
        ) : (
          // onBack still matters in home mode: if the catalog isn't organized
          // yet, the chat's blocked state offers "Back to Categorize" — send
          // that to the real setup flow.
          <CostsChat mode="real" home prefill={chatPrefill} onDone={() => navigate("/products")} onBack={() => navigate("/onboarding/chat")} />
        )}
      </section>

      <section style={{ marginBottom: 30 }}>
        <h2 style={sectionTitle}>Processing fees</h2>
        <p style={sectionSub}>
          What each payment processor takes per sale, as you described it during setup — shown here
          so it's never a mystery. To change a rate, tell the chat above.
        </p>
        <ProcessingFeesCard />
      </section>

      <section style={{ marginBottom: 30 }}>
        <h2 style={sectionTitle}>Inventory financing</h2>
        <p style={sectionSub}>
          If part of your stock is bought on a line of credit, the interest belongs in your margins
          and your cash cycle should only count the cash you actually front. Edit the terms here.
        </p>
        <InventoryFinancingCard canEdit={!isViewer} />
      </section>

      {/* Renders nothing when the setup chat parked no costs. */}
      <ParkedCostsCard canEdit={!isViewer} />

      <section style={{ marginBottom: 30 }}>
        <h2 style={sectionTitle}>Business costs</h2>
        <p style={sectionSub}>
          The costs that aren't tied to one product: your monthly overhead (with any setup-chat
          reminders folded in) and off-platform ad spend — set ongoing or one-time.
        </p>
        <BusinessCostsTab />
      </section>

      <section style={{ marginBottom: 30 }}>
        <h2 style={sectionTitle}>Cash timing</h2>
        <p style={sectionSub}>
          How long stock takes to arrive, and how long you get to pay for it — set per product,
          since different products (or manufacturers) can run on different timing. Powers the cash
          side of your Financials page.
        </p>
        <CashTimingCard />
      </section>

      <section style={{ marginBottom: 30 }}>
        <h2 style={sectionTitle}>Payouts &amp; reserves</h2>
        <p style={sectionSub}>
          For each payment processor: how fast it pays out and any reserve it holds back — both
          delay when your cash actually comes back. We blend them by each one's share of sales.
        </p>
        <PayoutReservesCard />
      </section>
    </div>
  );
}

const sectionTitle: React.CSSProperties = { fontSize: 15.5, fontWeight: 700, margin: "0 0 3px" };
const sectionSub: React.CSSProperties = { fontSize: 12.5, color: "var(--faint)", margin: "0 0 12px", lineHeight: 1.5 };
